import { useState } from 'react';
import { VideoCameraIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

interface VideoThumbnailProps {
  src: string;
  alt: string;
  className?: string;
}

type LoadState = 'loading' | 'loaded' | 'error';

/**
 * VideoThumbnail component with loading and error states.
 *
 * Shows a loading skeleton while the image loads, displays the image
 * on success, or falls back to a video icon placeholder on error.
 */
function VideoThumbnail({ src, alt, className }: VideoThumbnailProps) {
  const [loadState, setLoadState] = useState<LoadState>('loading');

  return (
    <div
      className={cn(
        'relative bg-dark-elevated-1 border border-border-subtle flex items-center justify-center overflow-hidden',
        className
      )}
    >
      {/* Loading skeleton / Error fallback */}
      {(loadState === 'loading' || loadState === 'error') && (
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center',
            loadState === 'loading' && 'animate-pulse'
          )}
        >
          <VideoCameraIcon className="h-12 w-12 text-text-muted" />
        </div>
      )}

      {/* Actual thumbnail image */}
      {src && (
        <img
          src={src}
          alt={alt}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-200',
            loadState === 'loaded' ? 'opacity-100' : 'opacity-0'
          )}
          onLoad={() => setLoadState('loaded')}
          onError={() => setLoadState('error')}
          loading="lazy"
        />
      )}
    </div>
  );
}

export default VideoThumbnail;
